import type { RelayGlobal, VoiceBundle, WidgetConfigResponse } from './protocol.js';

/**
 * The loader. Everything a visitor downloads before they decide to talk to anyone.
 *
 * Rules it must not break (WIDGET.md):
 *   - it must never break the customer's website
 *   - every failure is swallowed after at most one console warning
 *   - no global error handlers are installed
 *   - exactly one namespaced global is leaked
 *   - the heavy voice bundle is fetched only when the visitor clicks
 *
 * All markup lives in a shadow root so the customer's CSS cannot deform it and our CSS
 * cannot touch their page.
 */

declare const RELAY_API_BASE: string;

const NAMESPACE = 'VeraScaleRelay';
const WARNED = '[VeraScale Relay] widget unavailable';

type UiState = 'idle' | 'connecting' | 'live' | 'failed';

void main();

async function main(): Promise<void> {
  try {
    const script = currentScript();
    if (!script) return;

    const existing: RelayGlobal = window[NAMESPACE] ?? {};
    if (existing.loaded) return;
    existing.loaded = true;
    window[NAMESPACE] = existing;

    const siteKey = script.getAttribute('data-site-key');
    if (!siteKey) {
      warnOnce();
      return;
    }

    const apiBase = resolveApiBase(script);
    const config = await fetchConfig(apiBase, siteKey);
    if (!config) return;

    await whenBodyReady();
    mount({ apiBase, siteKey, config, voiceUrl: resolveVoiceUrl(script) });
  } catch {
    warnOnce();
  }
}

/**
 * document.currentScript is only valid while the script is executing, which is exactly now.
 * The fallback covers a customer who inlines or re-injects the tag.
 */
function currentScript(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement) return current;
  return document.querySelector<HTMLScriptElement>('script[data-site-key]');
}

function resolveApiBase(script: HTMLScriptElement): string {
  const override = script.getAttribute('data-api-base');
  if (override) return override.replace(/\/+$/, '');
  return RELAY_API_BASE.replace(/\/+$/, '');
}

/**
 * The voice bundle ships next to the loader, so its URL is derived rather than configured.
 *
 * Derived from `script.src` - the property, which the browser has already resolved to an
 * absolute URL - and not from `getAttribute('src')`, which returns whatever the customer
 * typed. Once the bundles live on their own host that difference decides whether this works
 * at all: a relative tag on the customer's page would otherwise resolve `voice.js` against
 * their site and 404.
 *
 * Both branches resolve against the loader's own URL for the same reason. `document.baseURI`
 * is the customer's page, which is never where our bundles are; it was only ever right
 * because dev serves both from one origin.
 */
function resolveVoiceUrl(script: HTMLScriptElement): string {
  const src = script.src;
  if (!src) return new URL('voice.js', document.baseURI).toString();
  if (src.includes('loader')) return src.replace(/loader(\.[a-z0-9]+)?\.js/, 'voice$1.js');
  return new URL('voice.js', src).toString();
}

async function fetchConfig(
  apiBase: string,
  siteKey: string,
): Promise<WidgetConfigResponse | null> {
  try {
    const url = `${apiBase}/api/v1/widget/config?siteKey=${encodeURIComponent(siteKey)}`;
    const response = await fetch(url, { method: 'GET', credentials: 'omit' });
    if (!response.ok) return warnOnce();
    const config = (await response.json()) as WidgetConfigResponse;
    return typeof config?.ctaText === 'string' ? config : warnOnce();
  } catch {
    return warnOnce();
  }
}

function whenBodyReady(): Promise<void> {
  if (document.body) return Promise.resolve();
  return new Promise((resolve) => {
    document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
  });
}

interface MountArgs {
  readonly apiBase: string;
  readonly siteKey: string;
  readonly config: WidgetConfigResponse;
  readonly voiceUrl: string;
}

function mount(args: MountArgs): void {
  const host = document.createElement('div');
  host.setAttribute('data-verascale-relay', '');
  const shadow = host.attachShadow({ mode: 'closed' });
  shadow.appendChild(styles(args.config));

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'cta';
  button.textContent = args.config.ctaText;

  const status = document.createElement('div');
  status.className = 'status';
  status.hidden = true;

  shadow.append(button, status);
  document.body.appendChild(host);

  let state: UiState = 'idle';
  let bundle: VoiceBundle | null = null;

  const render = (next: UiState, message?: string): void => {
    state = next;
    button.disabled = next === 'connecting';
    button.textContent =
      next === 'connecting'
        ? 'Connecting...'
        : next === 'live'
          ? 'End call'
          : args.config.ctaText;
    status.hidden = message === undefined;
    if (message !== undefined) status.textContent = message;
  };

  button.addEventListener('click', () => {
    void (async () => {
      try {
        if (state === 'live') {
          await bundle?.stop();
          render('idle');
          return;
        }
        if (state === 'connecting') return;

        render('connecting');
        bundle ??= await loadVoiceBundle(args.voiceUrl);
        await bundle.start({
          apiBase: args.apiBase,
          siteKey: args.siteKey,
          config: args.config,
          onState: (next) => render(next === 'ended' ? 'idle' : next),
          onFailure: () => render('failed', fallbackMessage(args.config)),
        });
      } catch {
        render('failed', fallbackMessage(args.config));
      }
    })();
  });
}

/**
 * The visitor asked to talk and we could not connect them. Give them the customer's phone
 * number rather than an error, because the point of the widget is not to lose the lead.
 */
function fallbackMessage(config: WidgetConfigResponse): string {
  return config.fallbackPhone
    ? `Sorry, we could not start the call. Please call ${config.fallbackPhone}.`
    : 'Sorry, we could not start the call. Please try again.';
}

async function loadVoiceBundle(url: string): Promise<VoiceBundle> {
  await new Promise<void>((resolve, reject) => {
    const tag = document.createElement('script');
    tag.src = url;
    tag.async = true;
    tag.addEventListener('load', () => resolve(), { once: true });
    tag.addEventListener('error', () => reject(new Error('voice bundle failed to load')), {
      once: true,
    });
    document.head.appendChild(tag);
  });

  const voice = window[NAMESPACE]?.__voice;
  if (!voice) throw new Error('voice bundle did not register');
  return voice;
}

function styles(config: WidgetConfigResponse): HTMLStyleElement {
  const el = document.createElement('style');
  const [vertical, horizontal] = config.position.split('-');
  el.textContent = `
    :host { all: initial; }
    .cta, .status {
      position: fixed;
      ${vertical === 'top' ? 'top' : 'bottom'}: 20px;
      ${horizontal === 'left' ? 'left' : 'right'}: 20px;
      z-index: 2147483000;
      font: 500 15px/1.4 system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    }
    .cta {
      padding: 12px 20px;
      border: 0;
      border-radius: 999px;
      color: #fff;
      background: ${cssColor(config.primaryColor)};
      cursor: pointer;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
    }
    .cta:disabled { opacity: 0.65; cursor: default; }
    .status {
      ${vertical === 'top' ? 'top' : 'bottom'}: 72px;
      max-width: 280px;
      padding: 10px 14px;
      border-radius: 10px;
      color: #111;
      background: #fff;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
    }
  `;
  return el;
}

/**
 * The colour comes from tenant configuration, so it is constrained before it reaches CSS.
 *
 * The fallback is the brand colour, and this is the copy that matters most: it is what
 * actually paints the button on a stranger's website when a tenant's stored value is
 * unreadable. It was teal long after nothing else was.
 */
function cssColor(value: string): string {
  return /^#[0-9a-f]{3,8}$/i.test(value) ? value : '#FF6B35';
}

function warnOnce(): null {
  try {
    console.warn(WARNED);
  } catch {
    /* a page that has replaced console is not our problem to solve */
  }
  return null;
}
